import { redirect } from "react-router";
import { supabase } from "@shared/integrations/supabase/client";
import { normalizeJudge } from "@/lib/judges";
import { normalizeCode } from "@/lib/codes";
import { isUserIdAdmin } from "@/lib/userRoles";
import type { Database as SupabaseDatabase } from "@/integrations/supabase/types";

export const WORKSPACE_LOADER_ID = "workspace-layout-loader";

const normalizeJudgesWithOwner = (
	rows: SupabaseDatabase["public"]["Tables"]["judges"]["Row"][],
	profiles: Record<string, string>,
) =>
	rows.map((judge) =>
		normalizeJudge(judge, profiles[judge.user_id] ?? "Unknown"),
	);

const normalizeCodesWithOwner = (
	rows: SupabaseDatabase["public"]["Tables"]["codes"]["Row"][],
	profiles: Record<string, string>,
) =>
	rows.map((code) => normalizeCode(code, profiles[code.user_id] ?? "Unknown"));

const normalizeDatasetsWithOwner = (
	rows: SupabaseDatabase["public"]["Tables"]["test_data_sets"]["Row"][],
	profiles: Record<string, string>,
) =>
	rows.map((dataset) => ({
		...dataset,
		owner_email: profiles[dataset.user_id] ?? "Unknown",
	}));

export const workspaceLayoutLoader = async () => {
	const {
		data: { session },
	} = await supabase.auth.getSession();

	if (!session?.user) {
		throw redirect("/auth");
	}

	const userId = session.user.id;

	// Check if user is admin
	const userIsAdmin = await isUserIdAdmin(userId);

	const [
		projectsRes,
		judgesRes,
		codesRes,
		datasetsRes,
		errorLogsRes,
	] = await Promise.all([
		supabase
			.from("projects")
			.select("*")
			.order("updated_at", { ascending: false }),
		// LLM agent rows are intentionally skipped in v1.1 demo mode.
		supabase
			.from("judges")
			.select("*")
			.order("updated_at", { ascending: false }),
		supabase
			.from("codes")
			.select("*")
			.order("updated_at", { ascending: false }),
		// If admin, fetch all datasets; otherwise only user's own and public datasets
		userIsAdmin
			? supabase
					.from("test_data_sets")
					.select("*")
					.order("updated_at", { ascending: false })
			: supabase
					.from("test_data_sets")
					.select("*")
					.or(`user_id.eq.${userId},is_public.eq.true`)
					.order("updated_at", { ascending: false }),
		supabase
			.from("error_logs")
			.select("id", { count: "exact", head: true })
			.eq("resolved", false),
	]);

	if (projectsRes.error) throw projectsRes.error;
	if (judgesRes.error) throw judgesRes.error;
	if (codesRes.error) throw codesRes.error;
	if (datasetsRes.error) throw datasetsRes.error;
	if (errorLogsRes.error) throw errorLogsRes.error;

	const projectRows = projectsRes.data ?? [];
	const judgeRows = judgesRes.data ?? [];
	const codeRows = codesRes.data ?? [];

	const profileUserIds = new Set<string>();
	for (const row of [...projectRows, ...judgeRows, ...codeRows]) {
		profileUserIds.add(row.user_id);
	}

	let profileMap: Record<string, string> = {};
	if (profileUserIds.size > 0) {
		const { data: profilesData, error: profilesError } = await supabase
			.from("profiles")
			.select("user_id, display_name, email")
			.in("user_id", [...profileUserIds]);

		if (profilesError) throw profilesError;

		profileMap = Object.fromEntries(
			(profilesData ?? []).map((profile) => [
				profile.user_id,
				profile.display_name ?? profile.email ?? "Unknown",
			]),
		);
	}

	const { data: profileData, error: profileError } = await supabase
		.from("profiles")
		.select("user_id, display_name, preferred_model")
		.eq("user_id", userId)
		.maybeSingle();

	if (profileError) {
		throw profileError;
	}

	// Normalize projects to flatten owner information
	const normalizedProjects = projectRows.map((project) => ({
		id: project.id,
		title: project.title,
		created_at: project.created_at,
		updated_at: project.updated_at,
		user_id: project.user_id,
		agent_id: project.agent_id,
		code_id: project.code_id,
		behavior_dataset_id: project.behavior_dataset_id,
		behavior_dataset_selected_at: project.behavior_dataset_selected_at,
		rules_dataset_id: project.rules_dataset_id,
		rules_dataset_selected_at: project.rules_dataset_selected_at,
		is_public: project.is_public,
		owner_email: profileMap[project.user_id] ?? "Unknown",
	}));

	return {
		user: session.user,
		projects: normalizedProjects,
		judges: normalizeJudgesWithOwner(judgeRows, profileMap),
		codes: normalizeCodesWithOwner(codeRows, profileMap),
		datasets: normalizeDatasetsWithOwner(datasetsRes.data ?? [], profileMap),
		errorLogsUnresolvedCount: errorLogsRes.count ?? 0,
		profile: profileData ?? null,
		userIsAdmin,
	} as const;
};

export type WorkspaceLoaderData = Awaited<
	ReturnType<typeof workspaceLayoutLoader>
>;
